import { redirect } from '@tanstack/react-router'
import { type Profile, useAuthStore } from '@/stores/auth-store'
import { canAccessPersonalizedPlanSettings } from '@/lib/personalized-plan-access'

export async function awaitAuthReady(): Promise<void> {
  const initialState = useAuthStore.getState()
  const shouldWaitForProfile =
    initialState.status === 'authenticated' && !initialState.profile

  if (
    initialState.status === 'loading' ||
    initialState.profileStatus === 'loading' ||
    shouldWaitForProfile
  ) {
    await new Promise<void>((resolve) => {
      const unsubscribe = useAuthStore.subscribe((state) => {
        const profileResolved =
          Boolean(state.profile) ||
          state.profileStatus === 'error' ||
          state.status === 'unauthenticated'
        const statusResolved = state.status !== 'loading'
        const profileStatusResolved = state.profileStatus !== 'loading'

        if (statusResolved && profileStatusResolved && profileResolved) {
          unsubscribe()
          resolve()
        }
      })
    })
  }
}

export async function requireAdmin(): Promise<void> {
  await awaitAuthReady()
  const { status, isAdminOrSuperAdmin, getHomePath } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!isAdminOrSuperAdmin()) {
    throw redirect({ to: getHomePath() })
  }
}
// Role-based access guards

/**
 * Require user to have specific role(s)
 * @param role
 * @returns
 */
export async function requireRole(
  role: Profile['role'] | Profile['role'][]
): Promise<void> {
  await awaitAuthReady()

  const { status, hasRole } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (Array.isArray(role)) {
    const hasAtLeastOneRole = role.some((r) => hasRole(r))
    if (!hasAtLeastOneRole) {
      throw redirect({ to: useAuthStore.getState().getHomePath() })
    }
    return
  }

  if (!hasRole(role)) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireMember(): Promise<void> {
  await awaitAuthReady()
  const { status, isMember } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!isMember()) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireTrainer(): Promise<void> {
  await awaitAuthReady()
  const { status, canAccessTrainerWorkspace } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }
  if (!canAccessTrainerWorkspace()) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireSuperAdmin(): Promise<void> {
  await awaitAuthReady()
  const { status, isSuperAdmin } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!isSuperAdmin()) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireAllowedPersonalizedPlanSuperAdmin(): Promise<void> {
  await awaitAuthReady()
  const { status, profile } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!canAccessPersonalizedPlanSettings(profile)) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireAdminOrSuperAdmin(): Promise<void> {
  await awaitAuthReady()
  const { status, isAdminOrSuperAdmin } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!isAdminOrSuperAdmin()) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}

export async function requireSystem(): Promise<void> {
  await awaitAuthReady()
  const { status, hasRole } = useAuthStore.getState()
  if (status === 'unauthenticated') {
    return
  }

  if (!hasRole('system')) {
    throw redirect({ to: useAuthStore.getState().getHomePath() })
  }
}
